<script>
import Layout from '@layouts/main.vue'
import PurchaseDialog from '@components/purchase-dialog.vue'
import OptInLabel from '@components/opt-in-label.vue'
import { authComputed, userMethods } from '@state/helpers'
import analyticsService from '@utils/analytics-service'
import { captureException } from '@sentry/browser'

export default {
  page() {
    return {
      title: 'Settings',
      meta: [
        {
          name: 'description',
          content: `The user profile for ${this.user.name}.`,
        },
      ],
    }
  },
  components: { Layout, PurchaseDialog, OptInLabel },
  props: {
    user: {
      type: Object,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
    optIns: {
      type: Array,
      required: true,
    },
    notificationServices: {
      type: Array,
      required: true,
    },
  },
  data() {
    const switchDict = {}
    const keyset =
      (this.user.tags.length > 0 ? this.user.tags : this.user.backup) || []
    for (let i = 0; i < this.tags.length; i += 1) {
      switchDict[this.tags[i].name] = keyset.includes(this.tags[i].name)
    }

    const optinSwitchDict = {}
    for (let i = 0; i < this.optIns.length; i += 1) {
      const optin = this.optIns[i].name || this.optIns[i]
      optinSwitchDict[optin] = this.user.optIns.includes(optin)
    }

    return {
      isWorking: false,
      error: null,
      userModel: JSON.parse(JSON.stringify(this.user)),
      tagData: this.tags,
      switchSettings: switchDict,
      optinChoices: optinSwitchDict,
      dialog: false,
      messageDialog: false,
      dialogTitle: '',
      showSubscriptions: '',
      upcLookup: {
        UPC_191726380788: 'Cocomelon Roto Plush Bedtime JJ Doll',
        UPC_192876961629: 'ASUS ROG Crosshair VIII Dark Hero',
        UPC_840006621638: 'Corsair 128GB (4x32GB) CMW128GX4M4E3200C16',
        UPC_850014119850: 'Arctis 7P Headphones (White)',
        UPC_850014119851: 'Arctis 7P Headphones (Black)',
      },
      productLookup: {
        'product_3b9363cd-5581-4094-9b81-d0b431d19b27':
          'Nintendo Switch Monster Hunter Rise Edition',
        'product_ac9eaee5-b00f-4a4b-968f-af818d39f77e':
          'Nintendo Switch w/ Neon Blue & Neon Red Joy-Con + Mario Kart 8 Deluxe (Full Game Download) + 3 Month Nintendo Switch Online Individual Membership',
      },
    }
  },
  computed: {
    ...authComputed,
    sortedTags() {
      const sorted = this.tagData

      sorted.sort(function(a, b) {
        return a.name.toString().localeCompare(b.name.toString())
      })

      return sorted
    },
    sourceIsStripe() {
      return (
        !this.user.subscriptionSource ||
        this.user.subscriptionSource === 'stripe'
      )
    },
    sortedOptIns() {
      const sorted = this.optIns

      sorted.sort(function(a, b) {
        return a.name.toString().localeCompare(b.name.toString())
      })

      return sorted
    },
    errorMessage() {
      if (!this.error) {
        return ''
      }

      if (
        this.error.response &&
        this.error.response.data &&
        this.error.response.data.error
      ) {
        return this.error.response.data.error
      }

      return 'There was an error updating your profile.'
    },
  },
  mounted() {
    analyticsService.trackPageView('Settings')
  },
  methods: {
    ...userMethods,
    tryUpdateUser() {
      this.isWorking = true
      this.error = null

      const newTags = []
      for (let i = 0; i < this.tagData.length; i += 1) {
        if (this.switchSettings[this.tagData[i].name] === true) {
          newTags.push(this.tagData[i].name)
        }
      }

      const newOptIns = []
      for (let i = 0; i < this.optIns.length; i += 1) {
        const name = this.optIns[i].name
        if (this.optinChoices[name] === true) {
          newOptIns.push(name)
        }
      }

      const accountedForTags = Object.keys(this.switchSettings)
      for (let i = 0; i < this.user.tags.length; i += 1) {
        if (accountedForTags.includes(this.user.tags[i]) === false) {
          newTags.push(this.user.tags[i])
        }
      }

      if (newTags.length > 0) {
        analyticsService.trackAction('User Subscribed to Tags', newTags)
      }
      if (newOptIns.length > 0) {
        analyticsService.trackAction('User opted in to', newTags)
      }

      this.userModel.tags = newTags
      this.userModel.optIns = newOptIns
      this.updateUser({ user: this.userModel })
        .then((res) => {
          const switchDict = {}
          for (let i = 0; i < this.tags.length; i += 1) {
            switchDict[this.tags[i].name] = res.tags.includes(this.tags[i].name)
          }

          this.switchSettings = switchDict
          this.userModel = res

          this.$swal.fire({
            title: 'Success!',
            text: 'Your settings have been successfully updated.',
            icon: 'success',
          })
        })
        .catch((err) => {
          this.error = err
          captureException(err)
        })
        .finally(() => {
          this.isWorking = false
        })
    },
    toggleNotifications() {
      if (
        this.userModel.subscriptionStatus !== 'active' &&
        this.userModel.subscriptionStatus !== 'trialing'
      ) {
        if (this.userModel.dropCount > 0) {
          this.$swal
            .fire({
              title: 'Please Note',
              text:
                'It looks like you still have drops remaining. These will be consumed until they hit 0, but your subscription billing cycle will begin immediately.',
              icon: 'info',
              confirmButtonText: 'OK',
            })
            .then(() => {
              analyticsService.trackAction('Presented User with Drops Dialog')
              this.showSubscriptions = this.userModel.showSubscription === true
              this.dialog = true
            })
        } else {
          if (this.isAppLogin) {
            window.location.href =
              'https://app.inventorybot.io/app/appRoutes/needSubscription'
          } else {
            analyticsService.trackAction('Presented User with Drops Dialog')
            this.showSubscriptions = this.userModel.showSubscription === true
            this.dialog = true
          }
        }
      }
    },
    userKeyIsSet(key) {
      return !!this.userModel.notifications[key].userKey
    },
    dialogIfNoUserKey(item) {
      if (this.userKeyIsSet(item.key)) {
        return
      }

      this.dialogTitle = 'You must provide a User Key first.'
      this.messageDialog = true
    },
    getTagTitle(item) {
      if (item.indexOf('UPC_') !== -1) {
        return this.upcLookup[item] || item
      }

      if (item.indexOf('product_') !== -1) {
        return this.productLookup[item] || item
      }

      return item
    },
    tryCancelSubscription() {
      if (this.user.subscriptionSource === 'apple') {
        window.open(
          'https://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/manageSubscriptions'
        )
        return
      }

      this.$swal
        .fire({
          title: 'Are you sure?',
          text:
            'Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.',
          icon: 'info',
          confirmButtonText: 'Yes',
          showCancelButton: true,
          cancelButtonText: 'No',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.cancelSubscription()
              .then(() => {
                this.$swal.fire({
                  title: 'Success!',
                  text: 'Your subscription has been successfully cancelled.',
                  icon: 'success',
                })
              })
              .catch((err) => {
                this.$swal.fire({
                  title: 'Error!',
                  text:
                    err && err.data
                      ? err.data.error
                      : 'An error occurred canceling your subscription.',
                  icon: 'error',
                })
              })
          }
        })
    },
  },
}
</script>

<template>
  <Layout>
    <h1>
      <BaseIcon name="user" />
      {{ userModel.username }}
      Profile
    </h1>
    <v-form :class="$style.theForm" @submit.prevent="tryUpdateUser">
      <v-row>
        <v-col>
          <v-row v-if="userModel.showSubscription">
            <v-col>
              <h3>Your Subscription</h3>
            </v-col>
          </v-row>

          <v-row v-if="userModel.showSubscription">
            <div>
              <v-alert
                border="bottom"
                colored-border
                :type="
                  userModel.subscriptionStatus === 'active'
                    ? 'success'
                    : 'warning'
                "
                elevation="2"
              >
                {{ userModel.subscriptionStatusMessage }}
              </v-alert>

              <BaseButton
                v-if="
                  sourceIsStripe &&
                    userModel.activeSubscription &&
                    (userModel.subscriptionStatus === 'active' ||
                      userModel.subscriptionStatus === 'trialing')
                "
                :disabled="isWorking"
                type="button"
                @click="tryCancelSubscription"
              >
                <BaseIcon v-if="isWorking" name="sync" spin />
                <span v-else>
                  Cancel Subscription
                </span>
              </BaseButton>
              <div v-else>
                <div v-if="userModel.subscriptionStatus === 'incomplete'">
                  <span>
                    Your subscription is still pending. If more than 30 minutes
                    have passed since activating your subscription, please
                    contact us and we will get it un-stuck.
                  </span>
                </div>
                <div v-else>
                  <div v-if="!sourceIsStripe && userModel.activeSubscription">
                    <span
                      >To manage your subscription, please go to the
                      'Subscriptions' section in your app store.</span
                    >
                  </div>
                  <BaseButton
                    v-if="sourceIsStripe"
                    type="button"
                    @click="toggleNotifications"
                  >
                    <span>
                      Start Subscription
                    </span>
                  </BaseButton>
                </div>
              </div>
            </div>
          </v-row>

          <v-row>
            <v-col></v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3>Global Settings</h3>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <div :class="$style.switchContainer">
                <label for="notificationsEnabled">Drops Enabled</label>
                <v-switch
                  v-model="userModel.notificationsEnabled"
                  :class="$style.noTopMargin"
                  name="notificationsEnabled"
                  inset
                  color="success"
                ></v-switch>
              </div>
              <div :class="$style.switchContainer">
                <label for="excludeBundles">Exclude Bundles</label>
                <v-switch
                  v-model="userModel.notifications.excludeBundles"
                  :disabled="!userModel.notificationsEnabled"
                  :class="$style.noTopMargin"
                  name="excludeBundles"
                  inset
                  color="success"
                ></v-switch>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-row>
            <v-col>
              <h3>Optional Settings</h3>
              <h5
                >Here you can opt-in to receive notifications from certain
                places that are turned off by default, or exclude certain things
                that are enabled by default.</h5
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col md="4">
              <div :class="$style.tagList">
                <div
                  v-for="item in sortedOptIns"
                  :key="item.name"
                  :class="$style.tagItem"
                >
                  <OptInLabel :opt-in="item" />
                  <div :class="$style.tagSwitch">
                    <v-switch
                      v-model="optinChoices[item.name]"
                      :class="$style.noTopMargin"
                      :disabled="!userModel.notificationsEnabled"
                      theme="bulma"
                      inset
                      color="success"
                    ></v-switch>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-row>
            <v-col>
              <h3>Notification Provider Settings</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="3">
              <div
                v-for="item in notificationServices"
                :key="item.key"
                :class="$style.notificationService"
              >
                <a v-if="item.icon" :href="item.url" target="_blank">
                  <img :class="$style.pushoverHeader" :src="item.icon" />
                </a>
                <a v-else :href="item.url" target="_blank">
                  <strong>{{ item.title }}</strong>
                </a>

                <div :class="$style.switchContainer">
                  <label>Enabled</label>
                  <v-switch
                    v-model="userModel.notifications[item.key].enabled"
                    :class="$style.noTopMargin"
                    :disabled="!userModel.notificationsEnabled"
                    inset
                    color="success"
                  ></v-switch>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-row>
            <v-col>
              <h3>Category Subscriptions</h3>
              <p
                >Choose what tags you would like to be notified about. More
                triggers means more notifications (and more Drops used), so try
                to be as specific as possible!</p
              >
              <p
                >Or if you're looking for specific products, you can register
                for those <BaseLink name="product-search">here</BaseLink></p
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col md="4">
              <div :class="$style.tagList">
                <div
                  v-for="item in sortedTags"
                  :key="item.name"
                  :class="$style.tagItem"
                >
                  <div :class="$style.tagLabel">
                    {{ getTagTitle(item.name) }} - {{ item.count }} Triggers
                  </div>
                  <div :class="$style.tagSwitch">
                    <v-switch
                      v-model="switchSettings[item.name]"
                      :class="$style.noTopMargin"
                      :disabled="!userModel.notificationsEnabled"
                      theme="bulma"
                      inset
                      color="success"
                    ></v-switch>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <BaseButton :disabled="isWorking" type="submit">
        <BaseIcon v-if="isWorking" name="sync" spin />
        <span v-else>
          Save
        </span>
      </BaseButton>
      <p v-if="error">
        {{ errorMessage }}
      </p>
    </v-form>

    <v-dialog v-model="dialog" max-width="600px">
      <PurchaseDialog
        title="Uh oh! You need an active subscription."
        :show-subscriptions="showSubscriptions"
      ></PurchaseDialog>
    </v-dialog>

    <v-dialog v-model="messageDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">
          {{ dialogTitle }}
        </v-card-title>
      </v-card>
    </v-dialog>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.tagList {
  display: flex;
  flex-direction: column;
  min-width: 400px;
}

.tagItem {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: 800;
}

.tagHint {
  font-size: 10px;
}

.switchContainer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 200px;
  font-weight: 800;
}

.tagLabel {
  justify-self: flex-start;
}

.tagSwitch {
  justify-self: flex-end;
}

.noTopMargin {
  margin-top: 0 !important;
}

.pushoverHeader {
  width: 200px;
  margin-bottom: 10px;
}

.hintLabel {
  position: relative;
  top: -20px;
}
</style>
